/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {Injectable, TrackByFunction} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {Subscription} from 'rxjs/Subscription';

import {FiltersProvider} from './filters';
import {Page} from './page';
import {Sort} from './sort';
export class Items {
/**
 * @param {?} _filters
 * @param {?} _sort
 * @param {?} _page
 */
constructor(private _filters: FiltersProvider,
private _sort: Sort,
private _page: Page) {}
/**
 * Indicates if the data is currently loading
 */
public loading = false;
/**
 * Tracking function to identify objects. Default is reference equality.
 */
public trackBy: TrackByFunction<any> = (index: number, item: any) => item;
/**
 * Subscriptions to the other providers changes.
 */
private _filtersSub: Subscription;
private _sortSub: Subscription;
private _pageSub: Subscription;
/**
 * Cleans up our subscriptions to other providers
 * @return {?}
 */
public destroy() {
        if (this._filtersSub) {
            this._filtersSub.unsubscribe();
        }
        if (this._sortSub) {
            this._sortSub.unsubscribe();
        }
        if (this._pageSub) {
            this._pageSub.unsubscribe();
        }
    }
/**
 * Whether we should use smart items for this datagrid or let the user handle
 * everything.
 */
private _smart = false;
/**
 * @return {?}
 */
public get smart(): boolean {
        return this._smart;
    }
/**
 * @return {?}
 */
public smartenUp() {
        this._smart = true;
        /*
         * These observers trigger a chain of function: filter -> sort -> paginate
         * An observer up the chain re-triggers all the operations that follow it.
         */
        this._filtersSub = this._filters.change.subscribe(() => this._filterItems());
        this._sortSub = this._sort.change.subscribe(() => {
            // Special case, if the datagrid went from sorted to unsorted, we have to re-filter
            // to get the original order back
            if (!this._sort.comparator) {
                this._filterItems();
            } else {
                this._sortItems();
            }
        });
        this._pageSub = this._page.change.subscribe(() => this._changePage());
    }
/**
 * List of all items in the datagrid
 */
private _all: any[];
/**
 * @return {?}
 */
public get all() {
        return this._all;
    }
/**
 * @param {?} items
 * @return {?}
 */
public set all(items: any[]) {
        this._all = items;
        this.emitAllChanges(items);
        if (this.smart) {
            this._filterItems();
        } else {
            this._displayed = items;
            this.emitChange();
        }
    }
/**
 * Manually recompute the list of displayed items
 * @return {?}
 */
public refresh() {
        if (this.smart) {
            this._filterItems();
        }
    }
/**
 * Internal temporary step, which we preserve to avoid re-filtering or re-sorting if not necessary
 */
private _filtered: any[];
/**
 * List of items currently displayed
 */
private _displayed: any[] = [];
/**
 * @return {?}
 */
public get displayed(): any[] {
        // Ideally we could return an immutable array, but we don't have it in Clarity yet.
        return this._displayed;
    }
/**
 * The Observable that lets other classes subscribe to items changes
 */
private _change = new Subject<any[]>();
/**
 * @return {?}
 */
private emitChange() {
        this._change.next(this.displayed);
    }
/**
 * @return {?}
 */
public get change(): Observable<any[]> {
        return this._change.asObservable();
    }
private _allChanges = new Subject<any[]>();
/**
 * @param {?} items
 * @return {?}
 */
private emitAllChanges(items: any[]): void {
        this._allChanges.next(items);
    }
/**
 * @return {?}
 */
public get allChanges(): Observable<any[]> {
        return this._allChanges.asObservable();
    }
/**
 * Checks if we don't have data to process yet, to abort early operations
 * @return {?}
 */
private get uninitialized() {
        return !this._all;
    }
/**
 * FiltersProvider items from the raw list
 * @return {?}
 */
private _filterItems() {
        if (this.uninitialized) {
            return;
        }
        if (this._filters.hasActiveFilters()) {
            this._filtered = this._all.filter((item) => this._filters.accepts(item));
        } else {
            // Work on a shallow copy of the array, to not modify the user's model
            this._filtered = this._all.slice();
        }
        this._page.totalItems = this._filtered.length;
        this._sortItems();
    }
/**
 * Sorts items in the filtered list
 * @return {?}
 */
private _sortItems() {
        if (this.uninitialized) {
            return;
        }
        if (this._sort.comparator) {
            this._filtered.sort((a, b) => this._sort.compare(a, b));
        }
        this._changePage();
    }
/**
 * Extracts the current page from the sorted list
 * @return {?}
 */
private _changePage() {
        if (this.uninitialized) {
            return;
        }
        if (this._page.size > 0) {
            this._displayed = this._filtered.slice(this._page.firstItem, this._page.lastItem + 1);
        } else {
            this._displayed = this._filtered;
        }
        this.emitChange();
    }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: FiltersProvider, },
{type: Sort, },
{type: Page, },
];
}

function Items_tsickle_Closure_declarations() {
/** @type {?} */
Items.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Items.ctorParameters;
/**
 * Indicates if the data is currently loading
 * @type {?}
 */
Items.prototype.loading;
/**
 * Tracking function to identify objects. Default is reference equality.
 * @type {?}
 */
Items.prototype.trackBy;
/**
 * Subscriptions to the other providers changes.
 * @type {?}
 */
Items.prototype._filtersSub;
/** @type {?} */
Items.prototype._sortSub;
/** @type {?} */
Items.prototype._pageSub;
/**
 * Whether we should use smart items for this datagrid or let the user handle
 * everything.
 * @type {?}
 */
Items.prototype._smart;
/**
 * List of all items in the datagrid
 * @type {?}
 */
Items.prototype._all;
/**
 * Internal temporary step, which we preserve to avoid re-filtering or re-sorting if not necessary
 * @type {?}
 */
Items.prototype._filtered;
/**
 * List of items currently displayed
 * @type {?}
 */
Items.prototype._displayed;
/**
 * The Observable that lets other classes subscribe to items changes
 * @type {?}
 */
Items.prototype._change;
/** @type {?} */
Items.prototype._allChanges;
/** @type {?} */
Items.prototype._filters;
/** @type {?} */
Items.prototype._sort;
/** @type {?} */
Items.prototype._page;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
